import { Spinner, useDisclosure } from '@chakra-ui/react'
import {
  faCaretDown,
  faPenToSquare,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useMemo } from 'react'
import DefaultModal from '../../../../../../components/DefaultModal'
import DropdownCell from '../../../../../../components/DropdownCell'
import DropdownMenu from '../../../../../../components/DropdownMenu'
import type {
  CurrencyDto,
  SwapLockedVariable,
  XToEurLockedVariable,
} from '../../../../../../types/coreApi-types'

const lockedVariables: SwapLockedVariable[] = ['AmountIn', 'AmountOut', 'Rate']
const xToEurLockedVariables: XToEurLockedVariable[] = [
  'WithdrawToEurRate',
  'DepositToEurRate',
]

interface SummaryRowProps {
  title: string
  value?: string | React.ReactNode
  children?: React.ReactNode
  disabled?: boolean
  editable?: {
    onChangeInput?: React.ChangeEventHandler<HTMLInputElement>
    onChangeTextArea?: React.ChangeEventHandler<HTMLTextAreaElement>
    value: string
    placeholder?: string
    onClickSave: () => Promise<any>
    isLoading: boolean
  }
  modalEditable?: {
    title: string
    description: string
  }
  showLockedVariablePicker?: boolean
  lockedVariable?: string | null
  setLockedVariable?: (variable: SwapLockedVariable | null) => void
  xToEurLockedVariable?: XToEurLockedVariable | null
  setXToEurLockedVariable?: (variable: XToEurLockedVariable | null) => void
  xToEurRateData?: {
    [key in XToEurLockedVariable as string]: {
      currency?: CurrencyDto
      rate?: number
    }
  }
  warning?: boolean
}

const SummaryRow: React.FC<SummaryRowProps> = ({
  title,
  value,
  editable,
  children,
  disabled,
  modalEditable,
  showLockedVariablePicker,
  lockedVariable,
  setLockedVariable,
  xToEurLockedVariable,
  setXToEurLockedVariable,
  xToEurRateData,
  warning,
}) => {
  const editModal = useDisclosure()

  const getXToEurRateUserFriendlyString = useMemo(
    () => (_lockedVariable: XToEurLockedVariable) => {
      if (!xToEurRateData) return ''
      return `${_lockedVariable}: ${xToEurRateData[_lockedVariable].currency?.isoCode} -> EUR = ${xToEurRateData[_lockedVariable].rate}`
    },
    [xToEurRateData]
  )

  const shouldShowXToEurLockedVariablePicker = useMemo(() => {
    return lockedVariable && lockedVariable !== 'Rate'
  }, [lockedVariable])

  return (
    <div className="flex flex-col border-b-[1px] justify-center border-solid border-[#2C383B]/10 text-[13px] min-h-[56px]">
      <div className="flex  justify-between">
        <p
          className={`pl-5 font-semi ${
            children || editable?.onChangeTextArea ? 'pt-4' : 'pt-2'
          }`}
        >
          {title}
        </p>
        {editable ? (
          modalEditable ? (
            <>
              <DefaultModal
                isLoading={false}
                isOpen={editModal.isOpen}
                onClose={() => {
                  editModal.onClose()
                  if (setLockedVariable) {
                    setLockedVariable(null)
                  }
                }}
                whiteBG
                maxW="500px"
              >
                <div className="p-5 text-description-large">
                  <p className="text-xl mb-2">{modalEditable.title}</p>
                  <p className="text-medium">{modalEditable.description}</p>

                  <input
                    onChange={editable.onChangeInput}
                    placeholder={editable.placeholder}
                    type="text"
                    value={editable.value}
                    className="mt-6 input"
                  />

                  {showLockedVariablePicker === true && (
                    <DropdownMenu
                      maxH="310px"
                      renderList={lockedVariables.map(lockedVariable => {
                        return (
                          <DropdownCell
                            key={lockedVariable}
                            title={lockedVariable}
                            onClick={() =>
                              setLockedVariable &&
                              setLockedVariable(lockedVariable)
                            }
                          />
                        )
                      })}
                    >
                      <div className="input text-start flex items-center pl-3 gap-2 mt-6">
                        {lockedVariable ?? 'Select a variable to lock'}

                        <div className="absolute right-0 top-0 bottom-0 flex items-center mr-3 text-brand-light-text-description">
                          <FontAwesomeIcon icon={faCaretDown} size="sm" />
                        </div>
                      </div>
                    </DropdownMenu>
                  )}

                  {shouldShowXToEurLockedVariablePicker === true && (
                    <DropdownMenu
                      maxH="310px"
                      renderList={xToEurLockedVariables.map(lockedVariable => {
                        return (
                          <DropdownCell
                            key={lockedVariable}
                            title={getXToEurRateUserFriendlyString(
                              lockedVariable
                            )}
                            onClick={() =>
                              setXToEurLockedVariable &&
                              setXToEurLockedVariable(lockedVariable)
                            }
                          />
                        )
                      })}
                    >
                      <div className="input text-start flex items-center pl-3 gap-2 mt-6">
                        {xToEurLockedVariable
                          ? getXToEurRateUserFriendlyString(
                              xToEurLockedVariable
                            )
                          : 'Select which EUR Rate to lock'}

                        <div className="absolute right-0 top-0 bottom-0 flex items-center mr-3 text-brand-light-text-description">
                          <FontAwesomeIcon icon={faCaretDown} size="sm" />
                        </div>
                      </div>
                    </DropdownMenu>
                  )}

                  <div className="flex gap-2 w-full mt-16">
                    <button
                      className="button-secondary"
                      onClick={editModal.onClose}
                    >
                      Cancel
                    </button>
                    <button
                      disabled={showLockedVariablePicker && !lockedVariable}
                      className="button"
                      onClick={async () => {
                        await editable.onClickSave()
                        editModal.onClose()
                      }}
                    >
                      {editable.isLoading ? <Spinner size="xs" /> : 'Save'}{' '}
                    </button>
                  </div>
                </div>
              </DefaultModal>

              <div className="flex items-center px-5">
                <div className="pr-4 font-normal text-brand-light-text-description">
                  {value}
                </div>

                <button
                  disabled={disabled}
                  onClick={editModal.onOpen}
                  className="bg-brand-app-menu text-white h-[32px] px-5 transition-all rounded disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faPenToSquare} className="text-sm" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 px-5 font-medium">
              {editable.onChangeInput && (
                <input
                  onChange={editable.onChangeInput}
                  placeholder={editable.placeholder}
                  type="text"
                  name="hash"
                  id="hash"
                  value={editable.value}
                  className="border-0 text-xs outline-0 bg-white h-[32px] rounded focus:outline-0"
                />
              )}

              {editable.onChangeTextArea && (
                <textarea
                  value={editable.value}
                  onChange={editable.onChangeTextArea}
                  placeholder={editable.placeholder}
                  className="border-0 text-xs outline-0 bg-white min-h-[50px] rounded focus:outline-0 my-2"
                />
              )}

              <button
                disabled={disabled}
                onClick={editable.onClickSave}
                className="bg-brand-app-menu text-white h-[32px] px-5 transition-all rounded disabled:cursor-not-allowed disabled:opacity-50"
              >
                {editable.isLoading ? <Spinner size="sm" /> : 'Save'}{' '}
              </button>
            </div>
          )
        ) : (
          <div className="flex items-center gap-2 px-5">
            <p
              className={`font-normal text-brand-light-text-description ${warning ? 'text-amber-600 font-semibold' : ''}`}
            >
              {value}
            </p>
            {warning && (
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="w-4 h-4 text-amber-500"
              />
            )}
          </div>
        )}
      </div>
      {children && <div className="px-5 pt-2 pb-4">{children}</div>}
    </div>
  )
}

export default SummaryRow
